import type { OrderLineStatus } from "@loot-os/fulfillment-core/order/types";

import { centsToDollars } from "../../lib/format";
import { lineStatusTagClass, statusTagText } from "../../lib/fulfillmentOrders";
import { type Dateish, toDate } from "../../lib/packing";
import { OrderTag } from "../OrderTag";

/** One physical card, as the page receives it from tRPC (dates arrive as ISO strings). */
export interface CardUnit {
  lineId: string;
  orderIndex: number;
  name: string;
  imageUrl: string | null;
  printing: string | null;
  valueCents: number;
  onHand: number | null;
  shopperName: string | null;
  orderCreatedAt: Dateish;
  /**
   * The line's status, shown as a tag when `showStatus` is set: the order list's card views hold
   * packed and cancelled cards too, where the pick sheet's are all still to pick.
   */
  status: OrderLineStatus;
}

/**
 * The combined card view, as Retool's *Cards View* renders it: **one cell per physical card**,
 * not a grouped count. Three copies of a card are three cards to find, and three cells to look
 * at — collapsing them to "x3" hides what a picker is holding. Sorted by card name, which is
 * what puts identical cards side by side on the bench.
 *
 * The image is the point of the screen: a picker matches artwork, so it gets the space.
 */
export function CardsView({
  units,
  showStatus = false,
}: {
  units: CardUnit[];
  showStatus?: boolean;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-4">
      {units.map((unit) => (
        <article
          key={unit.lineId}
          className={`flex flex-col gap-2 rounded-lg border bg-card p-3 break-inside-avoid ${
            showStatus && unit.status === "CANCELLED" ? "opacity-60" : ""
          }`}
        >
          <h3 className="text-sm font-semibold leading-snug">{unit.name}</h3>
          {showStatus ? (
            <OrderTag className={`${lineStatusTagClass(unit.status)} self-start`}>
              {statusTagText(unit.status)}
            </OrderTag>
          ) : null}
          <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            Order {unit.orderIndex}
          </div>
          {unit.printing ? (
            <div className="text-sm font-medium text-destructive">{unit.printing}</div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            ~{centsToDollars(unit.valueCents)} ~ {unit.onHand ?? "?"} on hand{" "}
            {unit.shopperName ?? "—"} - {toDate(unit.orderCreatedAt).toLocaleString()}
          </p>
          {unit.imageUrl ? (
            <img
              alt={unit.name}
              src={unit.imageUrl}
              loading="lazy"
              className="w-full rounded border bg-muted object-contain"
            />
          ) : (
            <div className="flex aspect-[3/4] w-full items-center justify-center rounded border bg-muted text-xs text-muted-foreground">
              no image
            </div>
          )}
        </article>
      ))}
      {units.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing to pick in this selection.</p>
      ) : null}
    </div>
  );
}
